/**
 * The group's wall.
 *
 * Newest first, paged by the same keyset cursor the gallery uses. Reactions are
 * applied optimistically — a like that waits for a round trip before it lights
 * up feels broken, and the failure path is a rollback plus a toast rather than
 * anything the user has to think about.
 */

import { useCallback, useMemo, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { trpc, trpcClient } from "../../lib/trpc.js";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { Sheet } from "../../components/ui/Sheet.js";
import { useToast } from "../../components/ui/Toast.js";
import { useAuth } from "../../lib/auth.js";
import { useGroupEvents } from "../../lib/useGroupEvents.js";
import { imageAccept } from "../../lib/imageSupport.js";
import { fetchSignedUrls } from "../../lib/signedUrls.js";
import { PostCard } from "./PostCard.js";
import { WallComposer } from "./WallComposer.js";
import type { Album } from "../album/types.js";
import {
  DISLIKE,
  LIKE,
  MAX_POST_PHOTOS,
  type Comment,
  type Post,
} from "./types.js";
import type { MediaRef } from "../../lib/useDecryptedUrl.js";
import "./WallPage.css";

const PAGE_SIZE = 20;

export function WallPage() {
  const { groupId } = useParams({ from: "/authed/g/$groupId/wall" });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();

  const [openThreads, setOpenThreads] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [composing, setComposing] = useState(false);

  // The wall is where someone else posting matters most, so it is live.
  useGroupEvents(groupId);

  const folders = useQuery(trpc.groups.folders.queryOptions({ groupId }));
  const albums = useMemo(
    () =>
      ((folders.data ?? []) as unknown as Album[]).filter(
        (f) => f.kind === "trip",
      ),
    [folders.data],
  );

  const group = useQuery(trpc.groups.get.queryOptions({ groupId }));
  const capabilities = useMemo(
    () => new Set(group.data?.capabilities ?? []),
    [group.data?.capabilities],
  );

  const feed = useInfiniteQuery({
    queryKey: ["wall", groupId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      trpcClient.social.posts.list.query({
        groupId,
        cursor: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (last) => last.nextCursor,
  });

  const posts = useMemo(
    () =>
      (feed.data?.pages ?? []).flatMap(
        (page) => page.items,
      ) as unknown as Post[],
    [feed.data],
  );

  /* ─────────────────────────── Attached photos ────────────────────────────── */

  const attachedIds = useMemo(() => {
    /*
     * Every photo the carousel can reach.
     *
     * This used to stop at four, from when a post showed a four-tile grid. The
     * carousel goes to `MAX_POST_PHOTOS`, so a fifth photo had a slide and no
     * URL — a permanently blank frame you could swipe to.
     */
    const ids = posts.flatMap((post) => post.fileIds.slice(0, MAX_POST_PHOTOS));
    return [...new Set(ids)];
  }, [posts]);

  const thumbs = useQuery({
    queryKey: ["wall-thumbs", groupId, attachedIds],
    enabled: attachedIds.length > 0,
    // Signed URLs are stable within their window, so this can be cached hard.
    staleTime: 10 * 60 * 1000,
    // Chunked: this is every photograph on every post the wall has loaded, so
    // it grows without limit as somebody scrolls.
    queryFn: async () => ({
      urls: await fetchSignedUrls(
        groupId,
        attachedIds,
        "thumb",
        await imageAccept(),
      ),
    }),
  });

  const thumbRefs = useMemo(() => {
    const map: Record<string, MediaRef> = {};
    for (const entry of thumbs.data?.urls ?? []) {
      if (entry.url) {
        map[entry.fileId] = {
          url: entry.url,
          wrappedFileKey: entry.wrappedFileKey ?? null,
          // The post frame is shaped from these, before the bytes arrive.
          width: entry.width,
          height: entry.height,
        };
      }
    }
    return map;
  }, [thumbs.data]);

  /* ───────────────────────────── Comments ─────────────────────────────────── */

  const threads = useQueries({
    queries: [...openThreads].map((postId) => ({
      queryKey: ["wall-comments", groupId, postId],
      queryFn: () =>
        trpcClient.social.comments.list.query({
          groupId,
          postId,
          fileId: null,
        }),
    })),
  });

  const commentsFor = useMemo(() => {
    const ids = [...openThreads];
    const map: Record<string, Comment[] | undefined> = {};
    ids.forEach((postId, position) => {
      map[postId] = threads[position]?.data as unknown as Comment[] | undefined;
    });
    return map;
  }, [openThreads, threads]);

  const refreshFeed = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["wall", groupId] }),
    [queryClient, groupId],
  );

  /* ───────────────────────────── Mutations ───────────────────────────────── */

  const createPost = useMutation({
    mutationFn: (input: {
      body: string;
      fileIds: string[];
      folderId: string | null;
    }) => trpcClient.social.posts.create.mutate({ groupId, ...input }),
    onSuccess: () => {
      void refreshFeed();
      // Attaching photos files them into the group, so the album counts and the
      // gallery are both stale the moment a post lands.
      void queryClient.invalidateQueries({ queryKey: ["gallery", groupId] });
      void queryClient.invalidateQueries({ queryKey: ["folders", groupId] });
    },
    onError: (error) => toast(error.message, { variant: "danger" }),
  });

  const deletePost = useMutation({
    mutationFn: (postId: string) =>
      trpcClient.social.posts.delete.mutate({ groupId, postId }),
    onSuccess: () => {
      void refreshFeed();
      toast("Post deleted.", { variant: "success" });
    },
    onError: (error) => toast(error.message, { variant: "danger" }),
  });

  const addComment = useMutation({
    mutationFn: (input: { postId: string; body: string }) =>
      trpcClient.social.comments.create.mutate({
        groupId,
        postId: input.postId,
        fileId: null,
        body: input.body,
      }),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: ["wall-comments", groupId, input.postId],
      });
      // The card shows a count, which the feed owns.
      void refreshFeed();
    },
    onError: (error) => toast(error.message, { variant: "danger" }),
  });

  const react = useMutation({
    mutationFn: (input: { postId: string; emoji: string; active: boolean }) =>
      trpcClient.social.reactions.set.mutate({
        groupId,
        targetType: "post",
        targetId: input.postId,
        emoji: input.emoji as "👍",
        active: input.active,
      }),
    onError: (error) => {
      void refreshFeed();
      toast(error.message, { variant: "danger" });
    },
    onSettled: () => refreshFeed(),
  });

  /**
   * Apply a reaction locally before the server hears about it.
   *
   * The mutual exclusion between 👍 and 👎 is mirrored here rather than waited
   * for, otherwise switching from like to dislike briefly shows both lit.
   */
  const toggleReaction = useCallback(
    (post: Post, emoji: string) => {
      const had = post.viewerReactions.includes(emoji);
      const opposite =
        emoji === LIKE ? DISLIKE : emoji === DISLIKE ? LIKE : null;

      queryClient.setQueryData(
        ["wall", groupId],
        (
          current:
            | { pages: { items: Post[]; nextCursor: string | null }[] }
            | undefined,
        ) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.map((entry) => {
                if (entry.id !== post.id) return entry;

                const bump = (
                  list: Post["reactions"],
                  target: string,
                  delta: number,
                ) => {
                  const found = list.find((r) => r.emoji === target);
                  if (!found)
                    return delta > 0
                      ? [...list, { emoji: target, count: 1 }]
                      : list;
                  return list
                    .map((r) =>
                      r.emoji === target ? { ...r, count: r.count + delta } : r,
                    )
                    .filter((r) => r.count > 0);
                };

                let reactions = bump(entry.reactions, emoji, had ? -1 : 1);
                let viewer = had
                  ? entry.viewerReactions.filter((value) => value !== emoji)
                  : [...entry.viewerReactions, emoji];

                if (
                  !had &&
                  opposite &&
                  entry.viewerReactions.includes(opposite)
                ) {
                  reactions = bump(reactions, opposite, -1);
                  viewer = viewer.filter((value) => value !== opposite);
                }

                return { ...entry, reactions, viewerReactions: viewer };
              }),
            })),
          };
        },
      );

      react.mutate({ postId: post.id, emoji, active: !had });
    },
    [queryClient, groupId, react],
  );

  const toggleThread = useCallback((postId: string) => {
    setOpenThreads((current) => {
      const next = new Set(current);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  }, []);

  /* ────────────────────────────── Render ─────────────────────────────────── */

  const canPost = capabilities.has("post:create");
  const myUserId = auth.user?.id;

  return (
    <div className="fc-wall">
      <div className="fc-wall__inner">
        {/*
        The header carries the action, and stays.

        The compose button used to float centred over the feed, fixed to the
        bottom of the viewport — which put it directly on top of whatever was
        there, and what was there was usually a post's comment box. You could
        watch it cover the field you were trying to type in. A button that
        obscures the content it sits on has to move, and a sticky header is
        where a primary action belongs: reachable at any scroll position without
        ever being over anything.
      */}
        <header className="fc-wall__header">
          <div className="fc-wall__heading">
            <h1>Wall</h1>
            <p className="fc-wall__subtitle">
              Everything posted in {group.data?.group.name ?? "this group"},
              newest first.
            </p>
          </div>

          {canPost && (
            <button
              type="button"
              className="fc-wall__compose"
              aria-expanded={composing}
              onClick={() => setComposing(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span>Write a post</span>
            </button>
          )}
        </header>

        {canPost && (
          <>
            <Sheet
              open={composing}
              onClose={() => setComposing(false)}
              title="New post"
              size="auto"
            >
              <WallComposer
                groupId={groupId}
                albums={albums}
                canUpload={capabilities.has("file:upload")}
                isPosting={createPost.isPending}
                onPost={(input) => {
                  createPost.mutate(input);
                  setComposing(false);
                }}
              />
            </Sheet>
          </>
        )}

        {feed.isLoading ? (
          <div className="fc-wall__feed">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton
                key={index}
                height="168px"
                radius="var(--fc-radius-lg)"
              />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <EmptyState
            icon={<WallIcon />}
            title="Nothing on the wall yet"
            description={
              canPost
                ? "Post an update, a plan, or a photo everyone should see."
                : "When someone posts, it will show up here."
            }
          />
        ) : (
          <>
            <div className="fc-wall__feed">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  thumbRefs={thumbRefs}
                  groupId={groupId}
                  comments={commentsFor[post.id]}
                  commentsOpen={openThreads.has(post.id)}
                  canComment={capabilities.has("comment:create")}
                  canReact={capabilities.has("reaction:create")}
                  canDelete={
                    capabilities.has("post:delete_any") ||
                    (capabilities.has("post:delete_own") &&
                      post.author?.id === myUserId)
                  }
                  isDeleting={
                    deletePost.isPending && deletePost.variables === post.id
                  }
                  onToggleComments={() => toggleThread(post.id)}
                  onReact={(emoji) => toggleReaction(post, emoji)}
                  onComment={(body) =>
                    addComment.mutate({ postId: post.id, body })
                  }
                  onDelete={() => deletePost.mutate(post.id)}
                  onOpenMedia={(fileId) =>
                    void navigate({
                      to: "/g/$groupId",
                      params: { groupId },
                      search: { photo: fileId },
                    })
                  }
                />
              ))}
            </div>

            {feed.hasNextPage && (
              <div className="fc-wall__more">
                <Button
                  variant="secondary"
                  loading={feed.isFetchingNextPage}
                  onClick={() => void feed.fetchNextPage()}
                >
                  Load older posts
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function WallIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 13h8M8 16.5h5" strokeLinecap="round" />
    </svg>
  );
}
